import { openDB, type DBSchema, type IDBPDatabase, type IDBPTransaction } from 'idb';
import type { CareCommand, CareState } from '../domain/types';

export interface OutboxEntry { command: CareCommand; status: 'pending' | 'conflict'; error?: string; sequence?: number }
export interface CachedCare { state: CareState; authoritative?: CareState; lastSyncedAt?: string; isSampleData?: boolean; storageRevision?: number }
export class CacheConflict extends Error { constructor() { super('Device records changed in another tab.'); } }
interface CareDatabase extends DBSchema {
  snapshots: { key: string; value: CachedCare };
  outbox: { key: string; value: OutboxEntry & { scope: string }; indexes: { 'by-scope': string } };
}
let database: Promise<IDBPDatabase<CareDatabase>> | undefined;
const STORAGE_WAIT_MS = 10_000;
const storageError = () => new Error('Browser storage did not respond. This change was not saved. Please try again; if it continues, close other Bella Tracker tabs and reopen the app.');
export function openCareDatabase(): Promise<IDBPDatabase<CareDatabase>> {
  if (database) return database;
  let expired = false;
  const opening = openDB<CareDatabase>('pawlog-care-v1', 1, { upgrade(db) {
    db.createObjectStore('snapshots');
    const store = db.createObjectStore('outbox');
    store.createIndex('by-scope', 'scope');
  }, blocking() { void opening.then(db => db.close()); database = undefined; }, terminated() { database = undefined; } });
  let timer: ReturnType<typeof setTimeout>;
  const pending = Promise.race([opening.then(db => { if (expired) db.close(); return db; }), new Promise<never>((_, reject) => {
    timer = setTimeout(() => { expired = true; reject(storageError()); }, STORAGE_WAIT_MS);
  })]).finally(() => clearTimeout(timer));
  database = pending;
  void pending.catch(() => { if (database === pending) database = undefined; });
  return pending;
}
/** Abort the actual transaction before releasing a failed save, so a retry cannot
 * race a late commit. Keep the deadline at the storage layer, not the form. */
async function storageTransaction<T>(tx: IDBPTransaction<CareDatabase, ['snapshots', 'outbox'], 'readonly' | 'readwrite'>, work: () => Promise<T>): Promise<T> {
  void tx.done.catch(() => undefined);
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      try { tx.abort(); } catch { return; } // Already committed: let the actual result settle.
      reject(storageError());
    }, STORAGE_WAIT_MS);
  });
  try { return await Promise.race([work(), deadline]); } finally { clearTimeout(timer!); }
}
export async function readCareCache(scope: string): Promise<{ snapshot?: CachedCare; outbox: OutboxEntry[] }> {
  const db = await openCareDatabase();
  const tx = db.transaction(['snapshots', 'outbox']);
  return storageTransaction(tx, async () => {
  const [snapshot, entries] = await Promise.all([tx.objectStore('snapshots').get(scope), tx.objectStore('outbox').index('by-scope').getAll(scope)]);
  await tx.done;
  return { snapshot, outbox: entries.map(({ scope: _, ...entry }) => entry).sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.command.happenedAt.localeCompare(b.command.happenedAt)) };
  });
}
/** Optimistic records and the replay command commit together, before UI success. */
export async function writeCareCache(scope: string, snapshot: CachedCare, outbox: OutboxEntry[], expectedRevision?: number): Promise<number> {
  const db = await openCareDatabase();
  const tx = db.transaction(['snapshots', 'outbox'], 'readwrite');
  return storageTransaction(tx, async () => {
  const existing = await tx.objectStore('snapshots').get(scope);
  const revision = existing?.storageRevision ?? 0;
  if (expectedRevision !== undefined && revision !== expectedRevision) { tx.abort(); await tx.done.catch(() => undefined); throw new CacheConflict(); }
  const keys = await tx.objectStore('outbox').index('by-scope').getAllKeys(scope);
  for (const key of keys) await tx.objectStore('outbox').delete(key);
  await tx.objectStore('snapshots').put({ ...snapshot, storageRevision: revision + 1 }, scope);
  for (const entry of outbox) await tx.objectStore('outbox').put({ ...entry, scope }, `${scope}:${entry.command.id}`);
  await tx.done;
  return revision + 1;
  });
}
export async function clearCareCache(scope: string, onlyWhenSynced = false): Promise<void> {
  const db = await openCareDatabase();
  const tx = db.transaction(['snapshots', 'outbox'], 'readwrite');
  return storageTransaction(tx, async () => {
  const keys = await tx.objectStore('outbox').index('by-scope').getAllKeys(scope);
  if (onlyWhenSynced && keys.length) { tx.abort(); await tx.done.catch(() => undefined); throw new Error('You have unsynced changes on this device. Connect to finish syncing or resolve conflicting changes before signing out.'); }
  for (const key of keys) await tx.objectStore('outbox').delete(key);
  await tx.objectStore('snapshots').delete(scope);
  await tx.done;
  });
}

/** Keep private vet-only records out of persistent browser storage. */
export function stripDogs(state: CareState, removeIds: Iterable<string>): CareState {
  const excluded = new Set(removeIds);
  const entityIds = new Set([...state.dogs, ...state.medications, ...state.administrations, ...state.episodes, ...state.grants].filter(item => excluded.has('dogId' in item ? item.dogId : item.id)).map(item => item.id));
  const dogs = state.dogs.filter(item => !excluded.has(item.id));
  return { ...state, dogs, medications: state.medications.filter(item => !excluded.has(item.dogId)), administrations: state.administrations.filter(item => !excluded.has(item.dogId)), episodes: state.episodes.filter(item => !excluded.has(item.dogId)), grants: state.grants.filter(item => !excluded.has(item.dogId)), audit: state.audit.filter(item => !entityIds.has(item.entityId)), selectedDogId: dogs.some(item => item.id === state.selectedDogId) ? state.selectedDogId : dogs[0]?.id || '' };
}
